import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import StepCIIU from '../components/onboarding/StepCIIU';
import { hoyISO } from '../lib/sso-documentos';
import {
  getSHTLabel,
  getParitarioLabel,
  type RiskLevel,
  type CompanySize,
} from '../lib/sst-rules';
import {
  perfilCompleto, filaFiguraSST, tecnicosRequeridos, paritarioRequerido, personalSaludRequerido,
  modalidadPorDefecto, aplicaFiguras, tamanoEmpresa, MODALIDAD_LABELS, MODALIDAD_CITAS, NOTA_TECNICOS_ADICIONALES,
  CITA_FIGURA, CITA_HORAS, type ModalidadSST,
} from '../lib/figuras-sst';
import { PREGUNTAS_SERVICIOS, type CampoServicios } from '../lib/servicios-permanentes';
import { useSedes } from '../lib/useSedes';
import { sedesActivas } from '../lib/sedes';

// Bloque 6: la figura SST, los técnicos, las horas y el organismo paritario se
// calculan con workers_count y risk_level (figuras-sst.ts), no se leen de
// sht_required ni paritario_type. Sin esos dos datos el perfil está incompleto.
interface SSOProfile {
  risk_level: RiskLevel | null;
  company_size: CompanySize | null;
  workers_count: number | null;            // DECLARADO por la empresa (normativa)
  workers_registered_count?: number | null; // registrados en StorSafe (activo o licencia; M39-bis, informativo)
  work_centers_count: number | null;
  has_remote_workers: boolean;
  has_priority_groups: boolean;
  modalidad_sst: ModalidadSST | null;
  // Servicios Permanentes (M17, bloque 7): boolean NULL, opcionales. Sin M17 no vienen.
  centro_a_mas_de_2km?: boolean | null;
  tiene_servicio_cocina?: boolean | null;
  trabajo_con_sustancias_o_suciedad?: boolean | null;
  trabajadores_cambian_ropa?: boolean | null;
  tiene_campamentos?: boolean | null;
  // Lote G (M51, GT 17): boolean NULL. Sin M51 no viene.
  transporta_quimicos_peligrosos?: boolean | null;
  status: 'pendiente' | 'validado';
  updated_at: string;
}

interface CiiuActivity {
  ciiu_code: string;
  is_primary: boolean;
  ciiu_activities: {
    description: string;
    section_name: string;
    risk_level: RiskLevel;
  };
}

interface Props {
  companyId: string;
  onEdit?: () => void;
  /** F1a: con 2 o más sedes activas, el número declarado lleva "Repartido en N sedes; ver Sedes". */
  onNavigateToSedes?: () => void;
}

const RISK_COLORS: Record<RiskLevel, { bg: string; color: string; border: string }> = {
  bajo:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.3)'  },
  medio: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  alto:  { bg: 'rgba(239,68,68,0.1)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)'  },
};

const RISK_LABELS: Record<RiskLevel, string> = {
  bajo: 'Bajo', medio: 'Medio', alto: 'Alto',
};

const COMPANY_SIZE_LABELS: Record<string, string> = {
  micro: 'Microempresa (1–9)',
  pequeña: 'Pequeña empresa (10–49)',
  mediana_a: 'Mediana empresa A (50–99)',
  mediana_b: 'Mediana empresa B (100–199)',
  grande: 'Gran empresa (200+)',
};

const NEXT_STEPS: Record<RiskLevel, { label: string; ref: string }[]> = {
  bajo: [
    { label: 'Diagnóstico inicial de riesgos laborales', ref: 'Art. 9 DE 255' },
    { label: 'Política de Seguridad y Salud en el Trabajo', ref: 'Art. 11 DE 255' },
    { label: 'Registro de accidentes e incidentes', ref: 'Art. 23 DE 255' },
  ],
  medio: [
    { label: 'Diagnóstico inicial de riesgos laborales', ref: 'Art. 9 DE 255' },
    { label: 'Política de Seguridad y Salud en el Trabajo', ref: 'Art. 11 DE 255' },
    { label: 'Plan de emergencias y evacuación', ref: 'Art. 26 DE 255' },
    { label: 'Registro de accidentes e incidentes', ref: 'Art. 23 DE 255' },
    { label: 'Capacitación en SST (mínimo 20 horas/año)', ref: 'AM 196' },
  ],
  alto: [
    { label: 'Diagnóstico inicial de riesgos laborales', ref: 'Art. 9 DE 255' },
    { label: 'Política de Seguridad y Salud en el Trabajo', ref: 'Art. 11 DE 255' },
    { label: 'Plan de emergencias y evacuación', ref: 'Art. 26 DE 255' },
    { label: 'Programa anual de Seguridad y Salud en el Trabajo', ref: 'Art. 12 DE 255' },
    { label: 'Médico empresarial y exámenes médicos ocupacionales', ref: 'Art. 20 DE 255' },
    { label: 'Reglamento interno de SST', ref: 'Art. 17 DE 255' },
    { label: 'Capacitación especializada en SST (mínimo 40 horas/año)', ref: 'AM 196' },
    { label: 'Identificación y evaluación de factores de riesgo químico', ref: 'AM 196 Anexo 1' },
  ],
};

interface StepStatus {
  id: string;
  label: string;
  normativa: string;
  completed: boolean;
}

function getSteps(riskLevel: RiskLevel, politicaCount: number, incidentCount: number, iperCount: number): StepStatus[] {
  return NEXT_STEPS[riskLevel].map(step => ({
    id: step.label,
    label: step.label,
    normativa: step.ref,
    completed:
      step.label === 'Diagnóstico inicial de riesgos laborales' ? iperCount > 0 :
      step.label === 'Política de Seguridad y Salud en el Trabajo' ? politicaCount > 0 :
      step.label === 'Registro de accidentes e incidentes' ? incidentCount > 0 :
      false,
  }));
}

export default function SSOProfilePage({ companyId, onNavigateToSedes }: Props) {
  const [profile, setProfile]       = useState<SSOProfile | null>(null);
  // Con una sola sede, guardar el número de trabajadores también actualiza el declarado de la Matriz (lo hace la base, M40).
  const { sedes, varias: variasSedes, recargar: recargarSedes } = useSedes(companyId);
  const [activities, setActivities] = useState<CiiuActivity[]>([]);
  const [loading, setLoading]       = useState(true);
  const [editMode, setEditMode]     = useState(false);
  const [politicaCount, setPoliticaCount]       = useState(0);
  const [incidentCount, setIncidentCount] = useState(0);
  const [iperCount, setIperCount]         = useState(0);
  const [guardandoModalidad, setGuardandoModalidad] = useState(false);
  const [errorModalidad, setErrorModalidad] = useState('');
  const [guardandoServicio, setGuardandoServicio] = useState<CampoServicios | null>(null);
  const [errorServicios, setErrorServicios] = useState('');
  const [guardandoTransporte, setGuardandoTransporte] = useState(false);
  const [estadoTransporte, setEstadoTransporte] = useState<'' | 'guardado' | string>('');

  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: prof }, { data: acts }, { count: politicaC }, { count: incidentC }, { count: iperC }] = await Promise.all([
      supabase
        .from('company_sso_profile')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle(),
      supabase
        .from('company_ciiu_activities')
        .select('ciiu_code, is_primary, ciiu_activities(description, section_name, risk_level)')
        .eq('company_id', companyId)
        .order('is_primary', { ascending: false }),
      // Política de SST: documento vigente del ítem GA-03 en Documentos SSO
      // (versión actual y sin vencer; sin "Vigente hasta" no vence).
      supabase
        .from('sso_documents')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('checklist_item_id', 'GA-03')
        .eq('is_current', true)
        .or(`expiry_date.is.null,expiry_date.gte.${hoyISO()}`),
      supabase
        .from('incidents')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId),
      supabase
        .from('iper_assessments')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId),
    ]);
    setProfile(prof ?? null);
    setActivities((acts as unknown as CiiuActivity[]) ?? []);
    setPoliticaCount(politicaC ?? 0);
    setIncidentCount(incidentC ?? 0);
    setIperCount(iperC ?? 0);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { if (companyId) loadData(); }, [companyId, loadData]);

  function handleEditDone() {
    setEditMode(false);
    loadData();
    recargarSedes();
  }

  // B2: quién gestiona la SST. Se guarda en company_sso_profile.modalidad_sst (M14).
  async function guardarModalidad(m: ModalidadSST) {
    setErrorModalidad('');
    setGuardandoModalidad(true);
    const { data, error } = await supabase
      .from('company_sso_profile')
      .update({ modalidad_sst: m, updated_at: new Date().toISOString() })
      .eq('company_id', companyId)
      .select('modalidad_sst');
    setGuardandoModalidad(false);
    if (error || !data || data.length === 0) {
      console.error('No se pudo guardar la modalidad SST:', error ?? 'sin filas (sin permiso)');
      setErrorModalidad('No se pudo guardar. Intenta de nuevo (solo el administrador o el gerente pueden cambiarla).');
      return;
    }
    setProfile(p => p ? { ...p, modalidad_sst: m } : p);
  }

  // C1 (bloque 7): preguntas de Servicios Permanentes. null = sin responder (el ítem queda "Sin calcular").
  async function guardarServicio(campo: CampoServicios, valor: boolean | null) {
    setErrorServicios('');
    setGuardandoServicio(campo);
    const { data, error } = await supabase
      .from('company_sso_profile')
      .update({ [campo]: valor, updated_at: new Date().toISOString() })
      .eq('company_id', companyId)
      .select(campo);
    setGuardandoServicio(null);
    if (error || !data || data.length === 0) {
      console.error(`No se pudo guardar ${campo}:`, error ?? 'sin filas (sin permiso)');
      const faltaM17 = error?.code === 'PGRST204' || error?.code === '42703';
      setErrorServicios(faltaM17
        ? 'Todavía no se puede guardar: falta actualizar la base de datos. Avisa al soporte de StorSafe.' /* M17 */
        : 'No se pudo guardar. Intenta de nuevo (solo el administrador o el gerente pueden cambiarlo).');
      return;
    }
    setProfile(p => p ? { ...p, [campo]: valor } : p);
  }

  // Lote G (M51): ¿la empresa transporta productos químicos peligrosos? Decide si GT 17 pide también la inspección de transporte.
  async function guardarTransporte(valor: boolean | null) {
    setEstadoTransporte('');
    setGuardandoTransporte(true);
    const { data, error } = await supabase
      .from('company_sso_profile')
      .update({ transporta_quimicos_peligrosos: valor, updated_at: new Date().toISOString() })
      .eq('company_id', companyId)
      .select('transporta_quimicos_peligrosos');
    setGuardandoTransporte(false);
    if (error || !data || data.length === 0) {
      console.error('No se pudo guardar transporta_quimicos_peligrosos:', error ?? 'sin filas (sin permiso)');
      const faltaM51 = error?.code === 'PGRST204' || error?.code === '42703';
      setEstadoTransporte(faltaM51
        ? 'Todavía no se puede guardar: falta actualizar la base de datos. Avisa al soporte de StorSafe.'
        : 'No se pudo guardar. Intenta de nuevo (solo el administrador o el gerente pueden cambiarlo).');
      return;
    }
    setProfile(p => p ? { ...p, transporta_quimicos_peligrosos: valor } : p);
    setEstadoTransporte('guardado');
  }

  if (editMode) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 0' }}>
        <div style={card}>
          <StepCIIU
            companyId={companyId}
            modo="perfil"
            onNext={handleEditDone}
            onBack={() => setEditMode(false)}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#475569' }}>
        Cargando perfil SST…
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 0' }}>
        <div style={{ ...card, textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
          <h2 style={{ color: '#f1f5f9', marginBottom: 8, fontSize: '1.2rem' }}>
            Perfil SST no configurado
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: 24 }}>
            Completa tu perfil de Seguridad y Salud en el Trabajo según el DE 255 y AM 196.
          </p>
          <button style={primaryBtn} onClick={() => setEditMode(true)}>
            Configurar perfil SST
          </button>
        </div>
      </div>
    );
  }

  // Perfil incompleto (bloque 6): sin trabajadores o sin nivel de riesgo no se calculan las figuras.
  if (!perfilCompleto(profile)) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 0' }}>
        <div style={{ ...card, textAlign: 'center', padding: '40px 32px' }}>
          <h2 style={{ color: '#f1f5f9', marginBottom: 8, fontSize: '1.15rem' }}>Completa tu Perfil SSO</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 6 }}>
            Faltan {[profile.workers_count ? null : 'el número de trabajadores', profile.risk_level ? null : 'el nivel de riesgo'].filter(Boolean).join(' y ')}.
          </p>
          <p style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 24 }}>
            Sin esos datos no se pueden calcular el responsable de SST, el organismo paritario ni las obligaciones del Checklist (DE 255 Arts. 18 y 32 a 34).
          </p>
          <button style={primaryBtn} onClick={() => setEditMode(true)}>Completar perfil SST</button>
        </div>
      </div>
    );
  }

  const trabajadores = profile.workers_count as number;
  const riesgo = profile.risk_level as RiskLevel;
  const fila = filaFiguraSST(trabajadores, riesgo);
  const nTecnicos = tecnicosRequeridos(trabajadores, riesgo);
  const paritario = paritarioRequerido(trabajadores);
  const modalidadPorDef = modalidadPorDefecto(trabajadores, riesgo);
  const modalidad = profile.modalidad_sst ?? modalidadPorDef;
  const avisoTecnico = aplicaFiguras(modalidad, fila.figura).avisoExigeTecnico;

  const steps = getSteps(riesgo, politicaCount, incidentCount, iperCount);
  const completedCount = steps.filter(s => s.completed).length;
  const progressStatus = completedCount === steps.length ? 'completado' : completedCount > 0 ? 'en_progreso' : 'pendiente';

  const rc = RISK_COLORS[riesgo];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Profile card */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>
              Perfil de Seguridad y Salud en el Trabajo
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.8rem' }}>
              Generado automáticamente según DE 255 y AM 196
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{
              background: progressStatus === 'completado' ? 'rgba(34,197,94,0.1)' : progressStatus === 'en_progreso' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)',
              color: progressStatus === 'completado' ? '#22c55e' : progressStatus === 'en_progreso' ? '#60a5fa' : '#f59e0b',
              border: `1px solid ${progressStatus === 'completado' ? 'rgba(34,197,94,0.3)' : progressStatus === 'en_progreso' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.3)'}`,
              borderRadius: 20, padding: '3px 12px', fontSize: '0.72rem', fontWeight: 700,
            }}>
              {progressStatus === 'completado' ? 'Completado' : progressStatus === 'en_progreso' ? 'En progreso' : 'Pendiente'}
            </span>
            <button style={secondaryBtn} onClick={() => setEditMode(true)}>
              Editar
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <InfoTile label="Nivel de riesgo">
            <span style={{
              background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`,
              borderRadius: 6, padding: '3px 12px', fontSize: '0.84rem', fontWeight: 700,
            }}>
              {RISK_LABELS[riesgo]}
            </span>
          </InfoTile>
          <InfoTile label="Tamaño empresa">
            <span style={{ color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 600 }}>
              {COMPANY_SIZE_LABELS[tamanoEmpresa(trabajadores)]}
            </span>
          </InfoTile>
          <InfoTile label="Responsable SST requerido">
            <span style={{ color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 600 }}>
              {getSHTLabel(fila.figura)}{fila.porCentro ? ' por lugar y/o centro de trabajo' : ''}
            </span>
            {fila.adicionalCada && (
              <span data-testid="tecnicos-requeridos" style={{ color: '#e2e8f0', fontSize: '0.82rem', display: 'block', marginTop: 4 }}>
                Técnicos requeridos: {nTecnicos} ({CITA_FIGURA})
                <span style={{ color: '#f59e0b', fontSize: '0.72rem', display: 'block' }}>{NOTA_TECNICOS_ADICIONALES}</span>
              </span>
            )}
            <span style={{ color: '#94a3b8', fontSize: '0.76rem', display: 'block', marginTop: 4 }}>
              Horas mínimas de gestión: {fila.horasMes != null ? `${fila.horasMes} horas/mes (${CITA_HORAS})` : `no confirmadas (la tabla del ${CITA_HORAS} disponible no trae esta fila)`}
            </span>
            <span style={{ color: '#475569', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>{CITA_FIGURA}</span>
          </InfoTile>
          <InfoTile label="Organismo paritario">
            <span style={{ color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 600 }}>
              {getParitarioLabel(paritario).split(' (')[0]}
            </span>
            <span style={{ color: '#475569', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>
              {getParitarioLabel(paritario).match(/\(([^)]+)\)/)?.[1] ?? ''} · con el total de trabajadores (por centro de trabajo: pendiente)
            </span>
          </InfoTile>
          <InfoTile label="Personal de salud requerido">
            {personalSaludRequerido(trabajadores).map(p => (
              <span key={p} style={{ color: '#e2e8f0', fontSize: '0.8rem', display: 'block' }}>{p}</span>
            ))}
            <span style={{ color: '#475569', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>{CITA_FIGURA}</span>
          </InfoTile>
        </div>

        {/* B2: el selector se ofrece siempre, con 3 opciones (decisión de JC, 23/10/2026). */}
        <fieldset style={{ border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', marginTop: 16 }}>
          <legend style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, padding: '0 6px' }}>
            ¿Quién gestiona la seguridad y salud en el trabajo?
          </legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(Object.keys(MODALIDAD_LABELS) as ModalidadSST[]).map(m => (
              <label key={m} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', minHeight: 40, cursor: guardandoModalidad ? 'wait' : 'pointer',
                border: `1px solid ${modalidad === m ? '#2FBF71' : '#334155'}`, borderRadius: 8,
                background: modalidad === m ? 'rgba(47,191,113,0.1)' : 'transparent', color: '#e2e8f0', fontSize: '0.84rem',
              }}>
                <input type="radio" name="modalidad_sst" value={m} checked={modalidad === m} disabled={guardandoModalidad}
                  onChange={() => guardarModalidad(m)} />
                <span>
                  {MODALIDAD_LABELS[m]}
                  <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>{MODALIDAD_CITAS[m]}</span>
                </span>
              </label>
            ))}
          </div>
          {!profile.modalidad_sst && (
            <p style={{ color: '#64748b', fontSize: '0.74rem', margin: '8px 0 0' }}>
              Por defecto: {MODALIDAD_LABELS[modalidadPorDef]} (la que exige el {CITA_FIGURA}). Elige otra si corresponde.
            </p>
          )}
          {avisoTecnico && (
            <p role="alert" style={{ color: '#fbbf24', fontSize: '0.8rem', margin: '8px 0 0' }}>
              Según el DE 255 Art. 18, tu empresa requiere técnico. Por eso en el Checklist siguen aplicando GA-05 y GA-07.
            </p>
          )}
          {errorModalidad && <p role="alert" style={{ color: '#fca5a5', fontSize: '0.8rem', margin: '8px 0 0' }}>{errorModalidad}</p>}
        </fieldset>

        {/* C1 (bloque 7): Servicios Permanentes del Anexo 1. Opcionales; sin respuesta el ítem queda "Sin calcular". */}
        <fieldset data-testid="preguntas-servicios" style={{ border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', marginTop: 16 }}>
          <legend style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, padding: '0 6px' }}>
            Servicios Permanentes (opcional)
          </legend>
          <p style={{ color: '#64748b', fontSize: '0.74rem', margin: '0 0 10px' }}>
            Deciden si aplican algunos ítems de Servicios Permanentes del Checklist. Sin respuesta, el ítem queda "Sin calcular" y no cuenta en el cumplimiento.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PREGUNTAS_SERVICIOS.map(q => {
              const valor = profile[q.campo] ?? null;
              const opciones: { v: boolean | null; label: string }[] = [{ v: true, label: 'Sí' }, { v: false, label: 'No' }, { v: null, label: 'Sin responder' }];
              return (
                <div key={q.campo} role="radiogroup" aria-label={q.pregunta}>
                  <p style={{ color: '#e2e8f0', fontSize: '0.84rem', margin: '0 0 4px', overflowWrap: 'anywhere' }}>{q.pregunta}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {opciones.map(o => (
                      <label key={String(o.v)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', minHeight: 40,
                        cursor: guardandoServicio ? 'wait' : 'pointer',
                        border: `1px solid ${valor === o.v ? '#2FBF71' : '#334155'}`, borderRadius: 8,
                        background: valor === o.v ? 'rgba(47,191,113,0.1)' : 'transparent', color: '#e2e8f0', fontSize: '0.82rem',
                      }}>
                        <input type="radio" name={q.campo} checked={valor === o.v} disabled={!!guardandoServicio}
                          onChange={() => guardarServicio(q.campo, o.v)} />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  <p style={{ color: '#475569', fontSize: '0.7rem', margin: '4px 0 0', overflowWrap: 'anywhere' }}>
                    {q.items.join(', ')} · {q.cita}{q.nota ? ` · ${q.nota}` : ''}
                  </p>
                </div>
              );
            })}
          </div>
          {errorServicios && <p role="alert" style={{ color: '#fca5a5', fontSize: '0.8rem', margin: '8px 0 0' }}>{errorServicios}</p>}
        </fieldset>

        {/* Lote G (M51): transporte de productos químicos peligrosos (GT 17). Opcional; sin respuesta se pide también el transporte. */}
        <fieldset data-testid="pregunta-transporte" style={{ border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', marginTop: 16 }}>
          <legend style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, padding: '0 6px' }}>Productos químicos (opcional)</legend>
          {(() => {
            const pregunta = '¿La empresa transporta productos químicos peligrosos (propios o con terceros)?';
            const valor = profile.transporta_quimicos_peligrosos ?? null;
            const opciones: { v: boolean | null; label: string }[] = [{ v: true, label: 'Sí' }, { v: false, label: 'No' }, { v: null, label: 'Sin responder' }];
            return (
              <div role="radiogroup" aria-label={pregunta}>
                <p style={{ color: '#e2e8f0', fontSize: '0.84rem', margin: '0 0 4px', overflowWrap: 'anywhere' }}>{pregunta}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {opciones.map(o => (
                    <label key={String(o.v)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', minHeight: 40, cursor: guardandoTransporte ? 'wait' : 'pointer',
                      border: `1px solid ${valor === o.v ? '#2FBF71' : '#334155'}`, borderRadius: 8,
                      background: valor === o.v ? 'rgba(47,191,113,0.1)' : 'transparent', color: '#e2e8f0', fontSize: '0.82rem',
                    }}>
                      <input type="radio" name="transporta_quimicos_peligrosos" checked={valor === o.v} disabled={guardandoTransporte}
                        onChange={() => guardarTransporte(o.v)} />
                      {o.label}
                    </label>
                  ))}
                </div>
                <p style={{ color: '#475569', fontSize: '0.7rem', margin: '4px 0 0', overflowWrap: 'anywhere' }}>
                  GT 17 · NTE INEN 2266:2013 · Si transporta (o no se responde), el Checklist pide también una inspección firmada de transporte; si no, cuenta solo el almacenamiento y el manejo.
                </p>
              </div>
            );
          })()}
          {guardandoTransporte && <p role="status" style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '8px 0 0' }}>Guardando…</p>}
          {estadoTransporte === 'guardado' && <p role="status" style={{ color: '#2FBF71', fontSize: '0.8rem', margin: '8px 0 0' }}>✓ Guardado</p>}
          {estadoTransporte && estadoTransporte !== 'guardado' && <p role="alert" style={{ color: '#fca5a5', fontSize: '0.8rem', margin: '8px 0 0' }}>{estadoTransporte}</p>}
        </fieldset>

        <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
            {profile.workers_count} trabajadores declarados
            {variasSedes && (
              <>
                {` · Repartido en ${sedesActivas(sedes).length} sedes; `}
                {onNavigateToSedes
                  ? <button type="button" onClick={onNavigateToSedes} style={linkBtn}>ver Sedes</button>
                  : 'ver Sedes'}
              </>
            )}
            {typeof profile.workers_registered_count === 'number' && ` · ${profile.workers_registered_count} registrados en StorSafe`}
            {profile.has_remote_workers && ' · incluye teletrabajadores'}
            {profile.has_priority_groups && ' · grupos de atención prioritaria'}
          </span>
          <span style={{ color: '#334155', fontSize: '0.8rem', marginLeft: 'auto' }}>
            Actualizado {new Date(profile.updated_at).toLocaleDateString('es-EC')}
          </span>
        </div>
      </div>

      {/* CIIU Activities */}
      {activities.length > 0 && (
        <div style={card}>
          <h3 style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 14 }}>
            ACTIVIDADES ECONÓMICAS CIIU
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activities.map((act) => {
              const info = act.ciiu_activities as unknown as { description: string; section_name: string; risk_level: RiskLevel };
              const rc2 = RISK_COLORS[info.risk_level];
              return (
                <div key={act.ciiu_code} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', flexWrap: 'wrap',
                  background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b', borderRadius: 8,
                }}>
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, minWidth: 68,
                    color: act.is_primary ? '#2FBF71' : '#64748b',
                  }}>
                    {act.is_primary ? 'PRINCIPAL' : 'SECUNDARIA'}
                  </span>
                  <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.88rem', marginRight: 4 }}>
                    {act.ciiu_code}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: '1 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>
                    {info.description}
                  </span>
                  <span style={{
                    background: rc2.bg, color: rc2.color, border: `1px solid ${rc2.border}`,
                    borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {RISK_LABELS[info.risk_level]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next steps */}
      <div style={card}>
        <h3 style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 14 }}>
          PRÓXIMOS PASOS OBLIGATORIOS — NIVEL {RISK_LABELS[riesgo].toUpperCase()}
          {completedCount > 0 && (
            <span style={{ marginLeft: 10, color: '#64748b', fontWeight: 500, letterSpacing: 0 }}>
              {completedCount}/{steps.length} completados
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.map((step, i) => (
            <div key={step.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: step.completed ? 'rgba(34,197,94,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${step.completed ? 'rgba(34,197,94,0.15)' : '#1e293b'}`,
              borderRadius: 8,
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: '50%',
                background: step.completed ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.12)',
                color: step.completed ? '#22c55e' : '#f59e0b',
                fontSize: '0.72rem', fontWeight: 700, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {step.completed ? '✓' : i + 1}
              </span>
              <span style={{
                color: step.completed ? '#475569' : '#cbd5e1',
                fontSize: '0.84rem', flex: 1,
                textDecoration: step.completed ? 'line-through' : 'none',
              }}>
                {step.label}
              </span>
              <span style={{ color: '#475569', fontSize: '0.72rem', flexShrink: 0 }}>{step.normativa}</span>
              {step.completed && (
                <span style={{
                  background: 'rgba(34,197,94,0.1)', color: '#22c55e',
                  border: '1px solid rgba(34,197,94,0.3)',
                  borderRadius: 20, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0,
                }}>
                  Completado
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b',
      borderRadius: 10, padding: '12px 14px',
    }}>
      <p style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600, marginBottom: 6, letterSpacing: '0.03em' }}>
        {label.toUpperCase()}
      </p>
      {children}
    </div>
  );
}

const card: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: '20px 24px',
};

const primaryBtn: React.CSSProperties = {
  padding: '11px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
  color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 'none', borderRadius: 8,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  padding: '7px 16px', background: 'transparent', border: '1px solid #334155',
  color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, color: '#7dd3fc', fontWeight: 600, fontSize: 'inherit',
  textDecoration: 'underline', cursor: 'pointer',
};
